import React, { useEffect, useState } from 'react';

interface AvailableSetting {
  setting_name: string;
  description: string;
}

interface Setting {
  setting_name: string;
  setting_value: string;
  description_text: string;
}

interface SettingsTableProps {
  apiBaseUrl: string;
  accessToken: string | null;
}

const SettingsTable: React.FC<SettingsTableProps> = ({ apiBaseUrl, accessToken }) => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [editedSettings, setEditedSettings] = useState<{ [key: string]: Setting }>({});
  const [availableSettings, setAvailableSettings] = useState<AvailableSetting[]>([]);
  const [settingSearch, setSettingSearch] = useState('');
    // Fetch available settings for dropdown
    const fetchAvailableSettings = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/settings/available-list`, {
          headers: {
            'Authorization': accessToken ? `Bearer ${accessToken}` : ''
          }
        });
        if (!res.ok) throw new Error('Failed to fetch available settings');
        const data = await res.json();
        setAvailableSettings(data);
      } catch (err) {
        // Optionally handle error
      }
    };

    useEffect(() => { fetchAvailableSettings(); }, [apiBaseUrl, accessToken]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<Setting>({ setting_name: '', setting_value: '', description_text: '' });
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/settings`, {
        headers: {
          'Authorization': accessToken ? `Bearer ${accessToken}` : ''
        }
      });
      if (!res.ok) throw new Error('Failed to fetch settings');
      const data = await res.json();
      setSettings(data);
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchSettings(); }, [apiBaseUrl, accessToken]);

  const handleAddChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setAddLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': accessToken ? `Bearer ${accessToken}` : ''
        },
        body: JSON.stringify(addForm)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to add setting');
      }
      setShowAdd(false);
      setAddForm({ setting_name: '', setting_value: '', description_text: '' });
      fetchSettings();
    } catch (err: any) {
      setAddError(err.message || 'Unknown error');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading settings...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;

  return (
    <div className="relative py-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Settings</h2>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div>
          <button
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-lg transition duration-300 ease-in-out mr-2"
            onClick={() => setShowAdd(true)}
          >
            Add New Setting
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded-md shadow-lg transition duration-300 ease-in-out flex items-center gap-2"
            onClick={async () => {
              if (Object.keys(editedSettings).length === 0) return;
              setLoading(true);
              setError(null);
              try {
                for (const key of Object.keys(editedSettings)) {
                  const setting = editedSettings[key];
                  const res = await fetch(`${apiBaseUrl}/settings/${encodeURIComponent(setting.setting_name)}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': accessToken ? `Bearer ${accessToken}` : ''
                    },
                    body: JSON.stringify({
                      setting_value: setting.setting_value,
                      description_text: setting.description_text
                    })
                  });
                  if (!res.ok) {
                    const data = await res.json();
                    throw new Error(data.error || 'Failed to update setting');
                  }
                }
                setEditedSettings({});
                setSaveSuccess('Settings saved successfully.');
                setTimeout(() => setSaveSuccess(null), 2000);
                fetchSettings();
              } catch (err: any) {
                setError(err.message || 'Unknown error');
                setSaveSuccess(null);
              } finally {
                setLoading(false);
              }
            }}
            disabled={
              loading ||
              Object.keys(editedSettings).length === 0 ||
              !Object.keys(editedSettings).some(key => {
                const orig = settings.find(s => s.setting_name === key);
                const edited = editedSettings[key];
                return orig && (
                  orig.setting_value !== edited.setting_value ||
                  orig.description_text !== edited.description_text
                );
              })
            }
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
        {saveSuccess && (
          <div className="text-green-600 text-right font-semibold">{saveSuccess}</div>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg shadow-md">
        <table className="min-w-full bg-white border border-gray-200 mb-6 rounded-lg shadow">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-4 text-left">Setting Name</th>
              <th className="py-2 px-4 text-left">Value</th>
              <th className="py-2 px-4 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {settings.length === 0 ? (
              <tr>
                <td className="py-4 px-4 text-center text-gray-400" colSpan={3}>No settings found.</td>
              </tr>
            ) : settings.map(setting => {
                const edited = editedSettings[setting.setting_name] || setting;
                return (
                  <tr key={setting.setting_name} className="hover:bg-gray-50">
                    <td className="py-2 px-4">{setting.setting_name}</td>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        className="border rounded px-2 py-1 w-full"
                        value={edited.setting_value}
                        onChange={e => setEditedSettings(prev => ({
                          ...prev,
                          [setting.setting_name]: {
                            ...edited,
                            setting_value: e.target.value
                          }
                        }))}
                      />
                    </td>
                    <td className="py-2 px-4 text-gray-600">
                      <textarea
                        className="border rounded px-2 py-1 w-full"
                        value={edited.description_text}
                        onChange={e => setEditedSettings(prev => ({
                          ...prev,
                          [setting.setting_name]: {
                            ...edited,
                            description_text: e.target.value
                          }
                        }))}
                        rows={2}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {showAdd && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <form onSubmit={handleAddSubmit} className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md relative">
            <h3 className="text-xl font-bold mb-4">Add New Setting</h3>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Setting Name</label>
              <input
                type="text"
                placeholder="Search settings..."
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-2"
                value={settingSearch}
                onChange={e => setSettingSearch(e.target.value)}
              />
              <select
                name="setting_name"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={addForm.setting_name}
                onChange={e => {
                  setAddForm({ ...addForm, setting_name: e.target.value });
                  // Optionally auto-fill description
                  const found = availableSettings.find(s => s.setting_name === e.target.value);
                  if (found) setAddForm(f => ({ ...f, description_text: found.description }));
                }}
                required
              >
                <option value="" disabled>Select a setting...</option>
                {availableSettings
                  .filter(s =>
                    s.setting_name.toLowerCase().includes(settingSearch.toLowerCase()) ||
                    s.description.toLowerCase().includes(settingSearch.toLowerCase())
                  )
                  .map(s => (
                    <option key={s.setting_name} value={s.setting_name}>
                      {s.setting_name} - {s.description}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Value</label>
              <input
                type="text"
                name="setting_value"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={addForm.setting_value}
                onChange={handleAddChange}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
              <textarea
                name="description_text"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                value={addForm.description_text}
                onChange={handleAddChange}
                rows={2}
                required
              />
            </div>
            {addError && <div className="mb-4 text-red-600">{addError}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md"
                onClick={() => { setShowAdd(false); setAddError(null); }}
                disabled={addLoading}
              >Cancel</button>
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                disabled={addLoading}
              >{addLoading ? 'Saving...' : 'Add'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SettingsTable;
